// app/components/Connectors/groww/OrderBook.tsx:
'use client'

import React, { useEffect, useState, useRef, useCallback, useMemo, memo } from 'react'
import styles from './OrderBook.module.css'
import { throttle } from 'lodash' // Ensure lodash.throttle is installed
import { usePathname } from 'next/navigation'
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { isMarketOpen, getTimeUntilMarketOpens, formatTimeDuration } from './utils/marketHours'
import { formatPrice } from '../utils/priceFormatter'

// --------------------------
// Type Declarations
// --------------------------
interface OrderItem {
  price: number
  size: number
}

interface AggregatedOrderItem {
  price: number
  size: number
  count: number
}

export interface OrderBookData {
  bids: OrderItem[]
  asks: OrderItem[]
}

// --------------------------
// OrderBookInner Component
// --------------------------
// This component handles the live data via WebSocket and renders the order book.
// IMPORTANT: It receives a key based on the symbol so that it fully remounts when the symbol changes.
interface OrderBookInnerProps {
  symbol: string
  onStateUpdate?: (isLoading: boolean, refreshData: () => void) => void
}

const OrderRow = memo(function OrderRow({
  ask,
  widthPercent,
  isAsk,
}: {
  ask: AggregatedOrderItem;
  widthPercent: number;
  isAsk: boolean;
}) {
  return (<div
    className={styles.orderRow}
    title={`Price: ${formatPrice(ask.price)}, Size: ${ask.size.toFixed(2)}, Total: ${formatPrice(ask.price * ask.size)}`}
  >
    <div className={`${styles.backgroundBar} ${isAsk ? styles.askBar : styles.bidBar}`} style={{ width: `${widthPercent}%` }}></div>
    <div className={styles.priceText}>{formatPrice(ask.price)}</div>
    <div className={styles.amountText}>{ask.size.toFixed(2)}</div>
  </div>);
});

interface OrderBookInnerRefType {
  refreshData: () => void;
  isLoading: boolean;
}

const OrderBookInner = memo(function OrderBookInner({ symbol, onStateUpdate }: OrderBookInnerProps) {
  // State for raw order book data
  const [data, setData] = useState<OrderBookData>({ bids: [], asks: [] })
  // State for aggregated data (to render)
  const [aggregatedData, setAggregatedData] = useState<{
    bids: AggregatedOrderItem[]
    asks: AggregatedOrderItem[]
  }>({ bids: [], asks: [] })
  // Loading state for polling
  const [isLoading, setIsLoading] = useState<boolean>(true)

  // Refs to manage scroll positions
  const asksListRef = useRef<HTMLDivElement>(null)
  const bidsListRef = useRef<HTMLDivElement>(null)
  
  // Function reference for fetchOrderBookData (will be defined in useEffect)
  const fetchOrderBookDataRef = useRef<() => Promise<void> | undefined>(undefined)
  
  // Function to manually refresh data
  const refreshData = useCallback(() => {
    if (isLoading) return
    fetchOrderBookDataRef.current && fetchOrderBookDataRef.current()
  }, [isLoading])
  
  // Notify parent component about state changes
  useEffect(() => {
    if (onStateUpdate) {
      onStateUpdate(isLoading, refreshData)
    }
  }, [isLoading, refreshData, onStateUpdate])

  // --------------------------
  // Polling for Order Book Data
  // --------------------------
  useEffect(() => {
    // Clear previous data on mount so we start fresh
    setData({ bids: [], asks: [] })

    // Create a throttled function to update data
    const throttledSetData = throttle((newData: OrderBookData) => {
      setData(newData)
    }, 100)

    let pollingInterval: NodeJS.Timeout | null = null
    let marketCheckInterval: NodeJS.Timeout | null = null
    let nextPollTimeout: NodeJS.Timeout | null = null
    let isMounted = true

    // Function to fetch order book data from Groww quotes API
    const fetchOrderBookData = async () => {
      // Store reference to this function for the refresh button
      fetchOrderBookDataRef.current = fetchOrderBookData
      if (!isMounted) return

      // Check if market is open before fetching
      if (!isMarketOpen() && !isLoading) {
        // Don't set loading if we're just checking market status
        return;
      }

      setIsLoading(true)
      
      try {
        // Format symbol for Groww API (add .NS suffix for NSE stocks)
        const formattedSymbol = symbol.toUpperCase().endsWith('.NS') || symbol.toUpperCase().endsWith('.BS')
          ? symbol.toUpperCase()
          : `${symbol.toUpperCase()}.NS`

        // Fetch quotes data from Groww API
        const response = await fetch('/api/connectors/groww/quotes', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            // We need to get the API key from localStorage since this component doesn't have access to the connector
            apiKey: localStorage.getItem('groww_api_key'),
            symbols: [formattedSymbol]
          })
        })

        if (!response.ok) {
          throw new Error('Failed to fetch order book data')
        }

        const quoteData = await response.json()
        
        // Extract depth data from the quote response
        if (quoteData && quoteData.depth) {
          const { buy = [], sell = [] } = quoteData.depth
          
          // Transform the data to match the expected OrderBookData format
          const newData: OrderBookData = {
            bids: Array.isArray(buy) ? buy.map((bid: { price: number | string, quantity: number | string }) => ({
              price: typeof bid.price === 'string' ? parseFloat(bid.price) : bid.price,
              size: typeof bid.quantity === 'string' ? parseFloat(bid.quantity) : bid.quantity
            })) : [],
            asks: Array.isArray(sell) ? sell.map((ask: { price: number | string, quantity: number | string }) => ({
              price: typeof ask.price === 'string' ? parseFloat(ask.price) : ask.price,
              size: typeof ask.quantity === 'string' ? parseFloat(ask.quantity) : ask.quantity
            })) : []
          }

          throttledSetData(newData)
        } else if (quoteData && quoteData.bid_price && quoteData.bid_quantity && quoteData.offer_price && quoteData.offer_quantity) {
          // If depth is not available but we have bid/ask prices, create a simple depth with one level
          const newData: OrderBookData = {
            bids: [{
              price: typeof quoteData.bid_price === 'string' ? parseFloat(quoteData.bid_price) : quoteData.bid_price,
              size: typeof quoteData.bid_quantity === 'string' ? parseFloat(quoteData.bid_quantity) : quoteData.bid_quantity
            }],
            asks: [{
              price: typeof quoteData.offer_price === 'string' ? parseFloat(quoteData.offer_price) : quoteData.offer_price,
              size: typeof quoteData.offer_quantity === 'string' ? parseFloat(quoteData.offer_quantity) : quoteData.offer_quantity
            }]
          }
          
          throttledSetData(newData)
        } else {
          console.warn('No depth data available for symbol:', formattedSymbol)
          // Set empty data if depth is not available
          throttledSetData({ bids: [], asks: [] })
        }
      } catch (error) {
        console.error('Error fetching order book data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    // Function to start polling
    const startPolling = () => {
      // Clear any existing interval
      if (pollingInterval) {
        clearInterval(pollingInterval)
      }
      
      // Fetch immediately
      fetchOrderBookData()
      
      // Set up polling interval (every 30 seconds)
      pollingInterval = setInterval(fetchOrderBookData, 30000)
    }
    
    // Function to schedule next polling when market opens
    const scheduleNextPolling = () => {
      if (nextPollTimeout) {
        clearTimeout(nextPollTimeout)
      }
      
      const timeUntilOpen = getTimeUntilMarketOpens()
      console.log(`Market is closed. Next order book poll scheduled in ${timeUntilOpen}ms`)
      
      // Set a timeout to start polling when market opens
      nextPollTimeout = setTimeout(() => {
        console.log('Market opened, starting order book polling')
        startPolling()
      }, timeUntilOpen)
    }
    
    // Check if market is open and start or schedule polling accordingly
    if (isMarketOpen()) {
      console.log('Market is open, starting order book polling')
      fetchOrderBookData()
      pollingInterval = setInterval(fetchOrderBookData, 30000)
      
      // Set up an interval to check if market closes
      marketCheckInterval = setInterval(() => {
        if (!isMarketOpen()) {
          console.log('Market closed, stopping order book polling')
          if (pollingInterval) {
            clearInterval(pollingInterval)
            pollingInterval = null
          }
          scheduleNextPolling()
        }
      }, 60000) // Check every minute if market is still open
    } else {
      console.log('Market is closed, scheduling next order book polling')
      scheduleNextPolling()
    }

    return () => {
      // Clean up on unmount
      isMounted = false
      if (pollingInterval) clearInterval(pollingInterval)
      if (marketCheckInterval) clearInterval(marketCheckInterval)
      if (nextPollTimeout) clearTimeout(nextPollTimeout)
      throttledSetData.cancel()
    }
  }, [symbol])

  // --------------------------
  // Direct Data Display Effect (No Aggregation)
  // --------------------------
  useEffect(() => {
    // Convert raw order items to the format needed for display
    const convertOrderItems = (orders: OrderItem[]): AggregatedOrderItem[] => {
      return orders.map(order => ({
        price: order.price,
        size: order.size,
        count: 1 // Each order is counted as 1 since we're not aggregating
      }))
    }

    // Sort bids in descending order (highest price first)
    let displayBids = convertOrderItems(data.bids).sort(
      (a, b) => b.price - a.price
    )
    
    // Sort asks in ascending order (lowest price first)
    let displayAsks = convertOrderItems(data.asks).sort(
      (a, b) => a.price - b.price
    )

    // No need to limit or pad - we'll show exactly what we get from the API
    setAggregatedData({ bids: displayBids, asks: displayAsks })

    // Optionally scroll the asks section to the bottom
    if (asksListRef.current) {
      asksListRef.current.scrollTop = asksListRef.current.scrollHeight
    }
  }, [data]) // Removed stepSize dependency since we're not using it

  // Determine maximum sizes for background bar scaling
  const maxBidSize = useMemo(() => Math.max(...aggregatedData.bids.map(bid => bid.size), 1), [aggregatedData.bids]);
  const maxAskSize = useMemo(() => Math.max(...aggregatedData.asks.map(ask => ask.size), 1), [aggregatedData.asks]);

  // Check if market is currently open
  const marketOpen = useMemo(() => isMarketOpen(), [data]);
  
  // Calculate time until market opens if closed
  const timeUntilOpen = useMemo(() => {
    if (marketOpen) return 0;
    return getTimeUntilMarketOpens();
  }, [marketOpen]);
  
  // Format the time until market opens
  const formattedTimeUntilOpen = useMemo(() => {
    if (marketOpen) return '';
    return formatTimeDuration(timeUntilOpen);
  }, [marketOpen, timeUntilOpen]);

  return (
    <div className={styles.mainContent}>
      {!marketOpen && (
        <div className={`${styles.marketClosedIndicator || 'marketClosedIndicator'}`}>
          <ClockIcon width={18} height={18} />
          <span>Market closed. Opens in {formattedTimeUntilOpen}</span>
        </div>
      )}
      {isLoading && aggregatedData.asks.length === 0 && aggregatedData.bids.length === 0 && (
        <div className={styles.loadingIndicator || 'loadingIndicator'}>
          Loading order book data...
        </div>
      )}
      {/* RENDER ASKS */}
      <div className={styles.asksSection} ref={asksListRef}>
        {aggregatedData.asks.map((ask, idx) => {
          // Calculate dynamic width based on order size relative to maximum size
          const widthPercent = maxAskSize > 0 ? (ask.size / maxAskSize) * 100 : 0
          return <OrderRow
            key={idx}
            ask={ask}
            widthPercent={widthPercent}
            isAsk={true}
          />
        })}
      </div>

      {/* Separator with Best Ask/Bid */}
      <div className={styles.separatorContainer}>
        <div className={styles.bestAsk}>
          {aggregatedData.asks[0]
            ? formatPrice(aggregatedData.asks[0].price)
            : 'N/A'}
        </div>
        <div className={styles.separator}></div>
        <div className={styles.bestBid}>
          {aggregatedData.bids[0]
            ? formatPrice(aggregatedData.bids[0].price)
            : 'N/A'}
        </div>
      </div>

      {/* RENDER BIDS */}
      <div className={styles.bidsSection} ref={bidsListRef}>
        {aggregatedData.bids.map((bid, idx) => {
          // Calculate dynamic width based on order size relative to maximum size
          const widthPercent = maxBidSize > 0 ? (bid.size / maxBidSize) * 100 : 0
          return <OrderRow
            key={idx}
            ask={bid}
            widthPercent={widthPercent}
            isAsk={false}
          />
        })}
      </div>
    </div>
  )
})

// --------------------------
// Parent OrderBook Component
// --------------------------
// This component manages the symbol selector and step‑size controls.
// It renders OrderBookInner with a key tied to the current symbol so that a change forces a remount.
export default function OrderBook() {
  const pathname = usePathname()
  // Extract the first segment after the domain from the URL path
  const pathSegments = pathname ? pathname.split('/').filter(Boolean) : []
  const symbolFromPath = pathSegments[0] || 'RELIANCE.NS'
  // If the symbol doesn't end with .NS or .BS, add .NS suffix for NSE stocks
  const symbolToUse =
    symbolFromPath.toUpperCase().endsWith('.NS') ||
    symbolFromPath.toUpperCase().endsWith('.BS')
      ? symbolFromPath
      : `${symbolFromPath}.NS`

  // State for symbol selection and custom input.
  const [currentSymbol, setCurrentSymbol] = useState(symbolToUse)
  const [isSymbolPopupOpen, setIsSymbolPopupOpen] = useState(false)
  const [symbolInput, setSymbolInput] = useState('')
  const [popularSymbols] = useState([
    'PAYTM.NS',
    'SWIGGY.NS',
    'HDFCBANK.NS',
    'INFY.NS',
    'TCS.NS'
  ])
  
  // State for tracking loading state and refresh function from the inner component
  const [innerState, setInnerState] = useState<{ isLoading: boolean, refreshData: () => void }>({ 
    isLoading: true, 
    refreshData: () => {} 
  })

  const handleSymbolChange = useCallback((newSymbol: string) => {
    const formattedSymbol = newSymbol.toUpperCase()
    setCurrentSymbol(formattedSymbol)
    setIsSymbolPopupOpen(false)
    setSymbolInput('')
  }, [])
  
  // Callback to receive state from OrderBookInner
  const handleInnerStateUpdate = useCallback((isLoading: boolean, refreshData: () => void) => {
    setInnerState({ isLoading, refreshData })
  }, [])

  return (
    <div className={styles.orderBookContainer}>
      {/* Header with Title and Controls */}
      <div className={styles.header}>
        <span className={styles.title}>Order Book</span>
        <div className={styles.controls}>
          {/* Refresh Button */}
          <button 
            onClick={() => innerState.refreshData()} 
            disabled={innerState.isLoading}
            className={styles.refreshButton || 'refreshButton'}
            title="Refresh order book data"
          >
            <ArrowPathIcon width={12} height={12} />
          </button>
          {/* Symbol Selector */}
          <div className={styles.symbolSelector}>
            <select
              value={currentSymbol}
              onChange={e => {
                if (e.target.value === 'custom') {
                  setIsSymbolPopupOpen(true)
                } else {
                  handleSymbolChange(e.target.value)
                }
              }}
              aria-label="Select Symbol"
              className={styles.symbolSelect}
            >
              <option value={currentSymbol}>
                {currentSymbol.toUpperCase()}
              </option>
              {popularSymbols.map(
                symbol =>
                  symbol !== currentSymbol.toUpperCase() && (
                    <option key={symbol} value={symbol}>
                      {symbol}
                    </option>
                  )
              )}
              <option value="custom">Custom</option>
            </select>
            {isSymbolPopupOpen && (
              <div className={styles.customSymbolPopup}>
                <input
                  type="text"
                  placeholder="Enter symbol (e.g. RELIANCE.NS)"
                  value={symbolInput}
                  onChange={e => setSymbolInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && symbolInput.trim()) {
                      handleSymbolChange(symbolInput.trim())
                    }
                  }}
                  className={styles.customSymbolInput}
                />
                <div className={styles.customSymbolButtons}>
                  <button
                    onClick={() => setIsSymbolPopupOpen(false)}
                    className={styles.cancelButton}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (symbolInput.trim()) {
                        handleSymbolChange(symbolInput.trim())
                      }
                    }}
                    className={styles.applyButton}
                    disabled={!symbolInput.trim()}
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Range Filter removed as we're showing raw data */}
        </div>
      </div>
      {/* 
          Using key={currentSymbol} forces a full remount of OrderBookInner
          when the symbol changes, so that no stale data persists.
      */}
      <OrderBookInner
        key={currentSymbol}
        symbol={currentSymbol}
        onStateUpdate={handleInnerStateUpdate}
      />
    </div>
  )
}
